import { create, StoreApi, UseBoundStore } from 'zustand';
import { toast } from 'react-toastify';

import getErrorMessage from '../utils/error';
import { IServiceStore } from '../types/types';
import { createService, deleteService, getServiceById, getServices, updateService } from '../utils/api';

const useServices: UseBoundStore<StoreApi<IServiceStore>> = create((set) => ({
  services: [],
  serviceById: null,
  saveLoading: false,
  fetchLoading: false,
  removeLoading: false,
  hasFetched: false,

  fetchServices: async () => {
    try {
      set({ fetchLoading: true });
      const { success, message, data } = await getServices();
      if (!success) throw new Error(message);
      set({
        services: data.sort((a: any, b: any) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()),
        hasFetched: true,
      });
      return { message, success };
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message, { position: 'top-right' });
      return { success: false, message };
    } finally {
      set({ fetchLoading: false });
    }
  },

  fetchServiceById: async (id) => {
    try {
      set({ fetchLoading: true });
      const { success, message, data } = await getServiceById(id);
      if (!success) throw new Error(message);
      set({ serviceById: data });
      return { message, success };
    } catch (error) {
      const message = getErrorMessage(error);
      return { success: false, message };
    } finally {
      set({ fetchLoading: false });
    }
  },

  addService: async (serviceData) => {
    try {
      set({ saveLoading: true });
      const { success, message, data } = await createService(serviceData);
      if (!success) throw new Error(message);
      set((state) => ({ services: state.services ? [...state.services, data] : [data] }));
      toast.success(message, {
        position: 'top-right',
      });
      return { message, success };
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message, { position: 'top-right' });
      return { success: false, message };
    } finally {
      set({ saveLoading: false });
    }
  },

  editService: async (id, serviceData) => {
    try {
      set({ saveLoading: true });
      const { success, message, data } = await updateService(id, serviceData);
      if (!success) throw new Error(message);
      set((state) => ({
        services: state.services?.map((service) => (service.service_id === id ? data : service)),
      }));
      toast.success(message, {
        position: 'top-right',
      });
      return { message, success };
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message, { position: 'top-right' });
      return { success: false, message };
    } finally {
      set({ saveLoading: false });
    }
  },

  removeService: async (id) => {
    try {
      set({ removeLoading: true });
      const { success, message } = await deleteService(id);
      if (!success) throw new Error(message);
      set((state) => ({
        services: state.services?.filter((service) => service.service_id !== id),
      }));
      toast.success(message, {
        position: 'top-right',
      });
      return { message, success };
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message, { position: 'top-right' });
      return { success: false, message };
    } finally {
      set({ removeLoading: false });
    }
  },
}));

export default useServices;
